/*
 * Shared WebAudio plumbing. Everything is synthesized at runtime — there are
 * no audio files, so nothing to license and nothing to download.
 */

export class AudioEngine {
  ctx: AudioContext | null = null;
  master!: GainNode;
  sfxBus!: GainNode;
  musicBus!: GainNode;
  private noiseBuffer: AudioBuffer | null = null;
  private sfxOn = true;
  private musicOn = true;

  /** Must be called from a user gesture (browsers block audio until then). */
  unlock(): void {
    if (!this.ctx) {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.8;
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.ratio.value = 4;
      this.master.connect(comp).connect(this.ctx.destination);
      this.sfxBus = this.ctx.createGain();
      this.musicBus = this.ctx.createGain();
      this.sfxBus.gain.value = this.sfxOn ? 0.9 : 0;
      this.musicBus.gain.value = this.musicOn ? 0.32 : 0;
      this.sfxBus.connect(this.master);
      this.musicBus.connect(this.master);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  get now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  setSfx(on: boolean): void {
    this.sfxOn = on;
    if (this.ctx) this.sfxBus.gain.setTargetAtTime(on ? 0.9 : 0, this.ctx.currentTime, 0.05);
  }

  setMusic(on: boolean): void {
    this.musicOn = on;
    if (this.ctx) this.musicBus.gain.setTargetAtTime(on ? 0.32 : 0, this.ctx.currentTime, 0.1);
  }

  /** Temporarily lower the music (voice callouts, big moments). */
  duck(amount: number, seconds: number): void {
    if (!this.ctx || !this.musicOn) return;
    const g = this.musicBus.gain;
    const t = this.ctx.currentTime;
    g.cancelScheduledValues(t);
    g.setTargetAtTime(0.32 * amount, t, 0.05);
    g.setTargetAtTime(0.32, t + seconds, 0.3);
  }

  suspend(): void {
    if (this.ctx && this.ctx.state === 'running') void this.ctx.suspend();
  }

  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  noise(): AudioBuffer | null {
    if (!this.ctx) return null;
    if (!this.noiseBuffer) {
      const len = this.ctx.sampleRate * 2;
      const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buf;
    }
    return this.noiseBuffer;
  }

  /** A single enveloped oscillator note. */
  tone(opts: {
    type?: OscillatorType;
    freq: number;
    endFreq?: number;
    at?: number;
    dur: number;
    gain?: number;
    attack?: number;
    bus?: AudioNode;
    detune?: number;
  }): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = opts.at ?? ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = opts.type ?? 'square';
    osc.frequency.setValueAtTime(opts.freq, t);
    if (opts.endFreq)
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, opts.endFreq), t + opts.dur);
    if (opts.detune) osc.detune.value = opts.detune;
    const peak = opts.gain ?? 0.2;
    const attack = opts.attack ?? 0.005;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + opts.dur);
    osc.connect(g).connect(opts.bus ?? this.sfxBus);
    osc.start(t);
    osc.stop(t + opts.dur + 0.02);
  }

  /** A burst of filtered noise. */
  hiss(opts: {
    at?: number;
    dur: number;
    gain?: number;
    filter?: BiquadFilterType;
    freq?: number;
    endFreq?: number;
    q?: number;
    attack?: number;
    bus?: AudioNode;
  }): void {
    const ctx = this.ctx;
    const buf = this.noise();
    if (!ctx || !buf) return;
    const t = opts.at ?? ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = opts.filter ?? 'bandpass';
    f.frequency.setValueAtTime(opts.freq ?? 1500, t);
    if (opts.endFreq) f.frequency.exponentialRampToValueAtTime(opts.endFreq, t + opts.dur);
    f.Q.value = opts.q ?? 1;
    const g = ctx.createGain();
    const peak = opts.gain ?? 0.2;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + (opts.attack ?? 0.01));
    g.gain.exponentialRampToValueAtTime(0.0001, t + opts.dur);
    src
      .connect(f)
      .connect(g)
      .connect(opts.bus ?? this.sfxBus);
    src.start(t, Math.random() * 1.5);
    src.stop(t + opts.dur + 0.05);
  }
}

export const midiToFreq = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);
